/**
 * ElicitationDialog — renders a form dialog when an MCP tool calls
 * mcpserver.Elicit on the backend, blocking the stream until the user
 * responds.
 *
 * The dialog reads from `usePendingElicitationsStore`. When the store
 * has a pending elicitation this component is open; on submit / decline /
 * cancel it POSTs to POST /sessions/{id}/elicitations and calls `close`.
 *
 * Form fields are derived from `requestedSchemaJson` (flat JSON Schema per
 * MCP 2025-06-18 spec):
 *   type: "string"                       → <Input>
 *   type: "string", enum: [...]          → <select> (native)
 *   type: "boolean"                      → <input type="checkbox">
 *   type: "number" | "integer"           → <Input type="number">
 *
 * Three buttons:
 *   Submit  → action=ACCEPT, content=<form values>
 *   Decline → action=DECLINE, content=null
 *   Cancel  → action=CANCEL, content=null
 *
 * On 204 the dialog closes and the pending entry is cleared.
 * On 4xx/5xx an error toast is shown and the dialog stays open for retry.
 *
 * English strings in code; all user-facing text via i18n translation keys.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/hooks/use-toast'
import { errorToast } from '@/hooks/errorToast'
import { useAuthStore } from '@/stores/auth'
import { usePendingElicitationsStore } from '@/stores/pendingElicitations'
import { apiUrl } from '@/api/client'
import { createLogger } from '@/utils/logger'
import { cn } from '@/lib/utils'

const logger = createLogger('ElicitationDialog')

// ============================================================================
// JSON Schema types (flat MCP 2025-06-18 subset)
// ============================================================================

type JSONSchemaPropertyType = 'string' | 'number' | 'integer' | 'boolean'

interface JSONSchemaProperty {
  type: JSONSchemaPropertyType
  description?: string
  enum?: string[]
}

interface FlatJSONSchema {
  type: 'object'
  properties?: Record<string, JSONSchemaProperty>
  required?: string[]
  description?: string
}

function parseFlatSchema(raw: string): FlatJSONSchema | null {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const schema = parsed as FlatJSONSchema
    // Minimal guard: must have type=object or at least properties
    if (schema.type !== 'object' && !schema.properties) return null
    return schema
  } catch {
    return null
  }
}

// ============================================================================
// POST /sessions/{id}/elicitations
// ============================================================================

interface ResolveElicitationPayload {
  elicitation_id: string
  action: 'accept' | 'decline' | 'cancel'
  content?: Record<string, unknown> | null
}

async function postElicitation(
  sessionId: string,
  payload: ResolveElicitationPayload,
  token: string | null,
): Promise<{ ok: boolean; status: number }> {
  const response = await fetch(
    `${apiUrl}/sessions/${encodeURIComponent(sessionId)}/elicitations`,
    {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    },
  )
  return { ok: response.ok, status: response.status }
}

// ============================================================================
// Field renderer
// ============================================================================

interface FieldRendererProps {
  name: string
  prop: JSONSchemaProperty
  value: string
  onChange: (name: string, value: string) => void
  disabled: boolean
  /** Whether this field is in the schema's `required` array. */
  required: boolean
  /** Whether this field is currently flagged missing after a failed ACCEPT submit. */
  missing: boolean
}

/**
 * RequiredAsterisk — visual `*` after the label for required fields.
 * Uses `text-destructive` so the same colour family as the
 * missing-field error border, which establishes the pairing for the
 * user.
 */
function RequiredAsterisk() {
  return <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
}

/**
 * MissingFieldNote — inline error text rendered below a field that
 * was flagged missing on the last ACCEPT submit. Tied to the field
 * via `aria-describedby` so screen readers surface the error
 * alongside the field, not as an unattached toast.
 */
function MissingFieldNote({ id, t }: { id: string; t: (k: string, opts?: Record<string, unknown>) => string }) {
  return (
    <p id={id} className="mt-1 text-xs text-destructive">
      {t('elicitation.error.fieldRequired', {
        defaultValue: 'This field is required.',
      })}
    </p>
  )
}

function FieldRenderer({ name, prop, value, onChange, disabled, required, missing }: FieldRendererProps) {
  const { t } = useTranslation()
  const label = prop.description ?? name
  const errorId = `${name}-error`
  // ariaProps merges three a11y attributes:
  //   - aria-required: announced on focus, BEFORE submission, so screen
  //     readers tell the user the field must be answered up-front (not
  //     just after a failed submit). Set whenever required is true.
  //   - aria-invalid + aria-describedby: post-submit error wiring; only
  //     set when the field has been flagged missing.
  const ariaProps: Record<string, unknown> = {}
  if (required) {
    ariaProps['aria-required'] = true
  }
  if (missing) {
    ariaProps['aria-invalid'] = true
    ariaProps['aria-describedby'] = errorId
  }
  // Border-colour ring when the field is missing — same destructive
  // hue as the asterisk and the toast, so the visual chain is
  // consistent.
  const errorBorder = missing ? 'border-destructive ring-2 ring-destructive/50' : ''

  if (prop.enum && prop.enum.length > 0) {
    return (
      <div>
        <label htmlFor={name} className="block text-sm font-medium text-neutral-900 mb-1.5">
          {label}
          {required && <RequiredAsterisk />}
        </label>
        <select
          id={name}
          value={value}
          onChange={(e) => onChange(name, e.target.value)}
          disabled={disabled}
          {...ariaProps}
          className={cn(
            'w-full min-h-[44px] px-4 py-2 rounded-lg text-base',
            'bg-white border border-neutral-200 transition-colors duration-150',
            'focus:outline-none focus:ring-2 focus:ring-primary',
            'disabled:opacity-50',
            errorBorder,
          )}
        >
          <option value="">{t('elicitation.pleaseSelect', { defaultValue: 'Please select…' })}</option>
          {prop.enum.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        {missing && <MissingFieldNote id={errorId} t={t} />}
      </div>
    )
  }

  if (prop.type === 'boolean') {
    return (
      <div>
        <div className="flex items-center gap-3">
          <input
            id={name}
            type="checkbox"
            checked={value === 'true'}
            onChange={(e) => onChange(name, String(e.target.checked))}
            disabled={disabled}
            {...ariaProps}
            className={cn(
              'w-5 h-5 rounded accent-primary disabled:opacity-50',
              missing && 'ring-2 ring-destructive ring-offset-1',
            )}
          />
          <label htmlFor={name} className="text-sm font-medium text-neutral-900">
            {label}
            {required && <RequiredAsterisk />}
          </label>
        </div>
        {missing && <MissingFieldNote id={errorId} t={t} />}
      </div>
    )
  }

  const inputType = prop.type === 'number' || prop.type === 'integer' ? 'number' : 'text'
  return (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-neutral-900 mb-1.5">
        {label}
        {required && <RequiredAsterisk />}
      </label>
      <Input
        id={name}
        type={inputType}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        disabled={disabled}
        {...ariaProps}
        className={cn('min-h-[44px]', errorBorder)}
      />
      {missing && <MissingFieldNote id={errorId} t={t} />}
    </div>
  )
}

// ============================================================================
// ElicitationDialog
// ============================================================================

export function ElicitationDialog() {
  const { t } = useTranslation()
  const pending = usePendingElicitationsStore((s) => s.pending)
  const close = usePendingElicitationsStore((s) => s.close)
  const token = useAuthStore((s) => s.accessToken)

  // field values keyed by property name
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [missingRequired, setMissingRequired] = useState<string[]>([])

  // Reset field values + validation state when a new elicitation
  // arrives (pending.elicitationId changed). Without this, a second
  // elicitation reaching the dialog while the first was still mounted
  // would inherit the prior elicitation's typed-in values.
  useEffect(() => {
    setFieldValues({})
    setMissingRequired([])
  }, [pending?.elicitationId])

  if (!pending) return null

  const schema = parseFlatSchema(pending.requestedSchemaJson)
  const properties = schema?.properties ?? {}
  const propertyNames = Object.keys(properties)
  const requiredFields = schema?.required ?? []

  const handleFieldChange = (name: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [name]: value }))
    // Clear the field's missing-flag as soon as the user types.
    if (missingRequired.includes(name)) {
      setMissingRequired((prev) => prev.filter((f) => f !== name))
    }
  }

  const handleAction = async (action: 'accept' | 'decline' | 'cancel') => {
    if (submitting) return

    // For ACCEPT only: validate required fields before submitting.
    // Decline / Cancel intentionally bypass validation (the user is
    // explicitly NOT providing answers; the tool handler ignores
    // content on those actions per spec).
    if (action === 'accept' && requiredFields.length > 0) {
      const missing = requiredFields.filter((name) => {
        const raw = fieldValues[name]
        const prop = properties[name]
        if (!prop) return true // declared required but not in properties — block submit
        if (prop.type === 'boolean') {
          // A required boolean is treated like "agree to terms" — the
          // user must explicitly INTERACT with the checkbox (move
          // `fieldValues[name]` from undefined to either 'true' or
          // 'false'). Unchecked-and-untouched is missing; the user
          // wanting to record an explicit "no" must click+unclick or
          // be presented with the boolean as a Yes/No enum. This
          // matches the chip-driven flow's no-typing principle —
          // ambiguous defaults are not counted as answers for
          // required clinical questions.
          return raw === undefined
        }
        return raw === undefined || raw === ''
      })
      if (missing.length > 0) {
        setMissingRequired(missing)
        toast({
          variant: 'destructive',
          description: t('elicitation.error.required', {
            defaultValue: 'Please fill in all required fields.',
          }),
        })
        return
      }
    }

    setSubmitting(true)

    let content: Record<string, unknown> | null = null
    if (action === 'accept') {
      // Build content object from field values.
      // Boolean fields are stored as string 'true'/'false'; convert back.
      const built: Record<string, unknown> = {}
      for (const [name, prop] of Object.entries(properties)) {
        const raw = fieldValues[name] ?? ''
        if (prop.type === 'boolean') {
          built[name] = raw === 'true'
        } else if (prop.type === 'number' || prop.type === 'integer') {
          built[name] = raw === '' ? null : Number(raw)
        } else {
          built[name] = raw
        }
      }
      content = built
    }

    const payload: ResolveElicitationPayload = {
      elicitation_id: pending.elicitationId,
      action,
      ...(content !== null ? { content } : {}),
    }

    try {
      const result = await postElicitation(pending.sessionId, payload, token)
      if (result.ok) {
        // 204 No Content — success
        close()
        setFieldValues({})
      } else if (result.status === 409) {
        // Already resolved — silently close (user probably double-clicked)
        logger.warn('elicitation_already_resolved', { elicitationId: pending.elicitationId })
        close()
        setFieldValues({})
      } else {
        errorToast({
          context: 'elicitationSubmit',
          description: t('elicitation.error.submit', {
            defaultValue: 'Failed to submit. Please try again.',
          }),
          onRetry: () => void handleAction(action),
        })
      }
    } catch (err) {
      logger.error('elicitation_post_failed', {
        elicitationId: pending.elicitationId,
        error: err instanceof Error ? err.message : 'unknown',
      })
      errorToast({
        context: 'elicitationSubmit',
        description: t('elicitation.error.network', {
          defaultValue: 'Network error. Please try again.',
        }),
        onRetry: () => void handleAction(action),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !submitting) void handleAction('cancel') }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{pending.message}</DialogTitle>
          {schema?.description && (
            <DialogDescription>{schema.description}</DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4 py-2">
          {propertyNames.map((name) => (
            <FieldRenderer
              key={name}
              name={name}
              prop={properties[name]}
              value={fieldValues[name] ?? ''}
              onChange={handleFieldChange}
              disabled={submitting}
              required={requiredFields.includes(name)}
              missing={missingRequired.includes(name)}
            />
          ))}
        </div>

        <div className="flex flex-col gap-2 pt-2">
          {propertyNames.length > 0 && (
            <Button
              onClick={() => void handleAction('accept')}
              disabled={submitting}
              className="w-full min-h-[44px] rounded-full"
            >
              {submitting
                ? t('common.submitting', { defaultValue: 'Submitting…' })
                : t('actions.submit', { defaultValue: 'Submit' })}
            </Button>
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => void handleAction('decline')}
              disabled={submitting}
              className="flex-1 min-h-[44px] rounded-full"
            >
              {t('elicitation.decline', { defaultValue: 'Decline' })}
            </Button>
            <Button
              variant="outline"
              onClick={() => void handleAction('cancel')}
              disabled={submitting}
              className="flex-1 min-h-[44px] rounded-full"
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
